// src/components/NewCollegeApplPreview.jsx
import React, { useState, useEffect } from "react";
import { CommonAxiosGet } from "../CommonUtils/CommonAxios";
import { config } from "../CommonUtils/CommonURLS";
import { PrintStyles, usePrintHandler, PrintHeader, SectionHeader, FieldGrid, DataTable, safeArray, formatYesNo } from "../CommonUtils/PrintPreview";

// ⚠️ NEW COLLEGE APPLICATION SPECIFIC FILE FIELDS - Only for this component
const NEW_COLLEGE_FILE_FIELDS = [
    "society_reg_certificate",
    "society_bylaws",
    "land_registered_doc",
    "land_sketch_doc",
    "building_plan_doc",
    "building_reg_doc",
    "building_approved_plan_doc",
    "construction_permission_doc",
    "building_completion_doc",
    "sanitary_certificate",
    "geo_tagged_photos",
    "annexure_doc",
    "parking_proof_doc",
    "playground_doc",
    "playground_mou_doc",
    "fdr_file",
    "dpr_document",
    "development_plan_doc",
    "governance_structure_doc",
    "finance_source_doc",
    "faculty_policies_doc"
];

// Field Definitions for New College Application
const SOCIETY_FIELDS = [
    { key: "society_name", label: "Society Name" },
    { key: "society_mobile", label: "Mobile Number" },
    { key: "society_district_name", label: "District" },
    { key: "society_mandal_name", label: "Mandal" },
    { key: "society_village_name", label: "Village" },
    { key: "society_secretariat_name", label: "Secretariat" },
    { key: "society_street", label: "Street" },
    { key: "society_door_no", label: "Door No" },
    { key: "society_reg_certificate", label: "Registration Certificate" },
    { key: "society_bylaws", label: "Bye-laws of Society" },
];

const SOCIETY_STATUS_FIELDS = [
    { key: "is_other_institution", label: "Any Other Educational Institution?" },
    { key: "other_institution_details", label: "Other Institution Details" },
    { key: "is_temp_college", label: "Temporary Degree College?" },
    { key: "temp_college_details", label: "Temporary College Details" },
    { key: "has_criminal_record", label: "Criminal Record?" },
    { key: "criminal_record_details", label: "Criminal Record Details" },
    { key: "is_ugc_not_recognized", label: "UGC Not Recognized?" },
    { key: "ugc_not_recognized_details", label: "UGC Not Recognized Details" },
    { key: "has_dues_complaints", label: "Dues/Complaints?" },
    { key: "dues_complaints_details", label: "Dues/Complaints Details" },
];

const COLLEGE_FIELDS = [
    { key: "proposed_college_name", label: "Proposed College Name" },
    { key: "college_district_name", label: "Proposed College District" },
    { key: "college_mandal_name", label: "Proposed College Mandal" },
    { key: "college_village_name", label: "Proposed College Village" },
    { key: "college_secretariat_name", label: "Proposed College Secretariat" },
    { key: "college_survey_no", label: "Survey No" },
    { key: "college_door_no", label: "Door No" },
    { key: "college_street", label: "Street" },
    { key: "college_address_district_name", label: "Postal District" },
    { key: "college_address_mandal_name", label: "Postal Mandal" },
    { key: "college_address_village_name", label: "Postal Village" },
    { key: "college_address_secretariat_name", label: "Postal Secretariat" },
    { key: "college_mobile", label: "College Mobile" },
    { key: "college_email", label: "College Email" },
    { key: "mandal_status", label: "Status of Mandal" },
];

const SECRETARY_FIELDS = [
    { key: "secretary_name", label: "Name" },
    { key: "secretary_designation", label: "Designation" },
    { key: "secretary_mobile", label: "Mobile Number" },
    { key: "secretary_email", label: "Email" },
    { key: "secretary_address", label: "Address" },
];

const PRINCIPAL_FIELDS = [
    { key: "principal_name", label: "Name" },
    { key: "principal_designation", label: "Designation" },
    { key: "principal_mobile", label: "Mobile Number" },
    { key: "principal_email", label: "Email" },
    { key: "principal_address", label: "Address" },
];

const LAND_BASIC_FIELDS = [
    { key: "is_own_land", label: "Society has own land?" },
    { key: "land_extent", label: "Extent of Land (Acres)" },
];

const LAND_DETAILS_FIELDS = [
    { key: "land_survey_no", label: "Survey No" },
    { key: "land_district_name", label: "District" },
    { key: "land_mandal_name", label: "Mandal" },
    { key: "land_village_name", label: "Village" },
    { key: "is_land_contiguous", label: "Land is Contiguous?" },
    { key: "non_contiguous_details", label: "Non-Contiguous Details" },
    { key: "land_distance", label: "Distance Between Bits (KM)" },
];

const LAND_DOCUMENTS_FIELDS = [
    { key: "land_registered_doc", label: "Registered Document" },
    { key: "land_sketch_doc", label: "Sketch Plan" },
];

const BUILDING_BASIC_FIELDS = [
    { key: "type_of_accommodation", label: "Type of Accommodation" },
    { key: "building_survey_no", label: "Survey No" },
    { key: "building_door_no", label: "Door No" },
    { key: "building_district_name", label: "District" },
    { key: "building_mandal_name", label: "Mandal" },
    { key: "building_village_name", label: "Village" },
    { key: "building_carpet_area", label: "Carpet Area (Sq Ft)" },
];

const BUILDING_APPROVAL_FIELDS = [
    { key: "is_building_plan_approved", label: "Building Plan Approved?" },
    { key: "competent_authority", label: "Competent Authority" },
    { key: "is_near_restricted_area", label: "Within 500m of Restricted Area?" },
    { key: "is_improper_building", label: "Building in Flats/Sheds/Commercial?" },
    { key: "is_other_institution_building", label: "Other Institution in Same Building?" },
    { key: "other_institution_in_building_details", label: "Other Institution Details" },
];

const BUILDING_DOCUMENTS_FIELDS = [
    { key: "building_plan_doc", label: "Building Plan" },
    { key: "building_reg_doc", label: "Registered Document" },
    { key: "building_reg_date", label: "Date of Registration" },
    { key: "building_approved_plan_doc", label: "Approved Plan by Licensed Surveyor" },
    { key: "construction_permission_doc", label: "Construction Permission Order" },
    { key: "building_completion_doc", label: "Building Completion/Occupancy Certificate" },
    { key: "sanitary_certificate", label: "Valid Sanitary Certificate" },
    { key: "geo_tagged_photos", label: "Geotagged Photographs" },
    { key: "annexure_doc", label: "Room-wise Details (Annexure-I)" },
];

const PARKING_FIELDS = [
    { key: "is_parking_available", label: "Parking Available?" },
    { key: "is_parking_size_20_percent", label: "20% Area Provided?" },
    { key: "parking_proof_doc", label: "Parking Proof" },
];

const PLAYGROUND_FIELDS = [
    { key: "is_playground_available", label: "Playground Available?" },
    { key: "playground_extent", label: "Playground Extent (Sq Ft)" },
    { key: "playground_doc", label: "Playground Document" },
    { key: "has_alternative_playground", label: "Alternative Playground?" },
    { key: "playground_mou_doc", label: "Playground MoU Document" },
];

const UG_PROGRAM_COLS = [
    { key: "program_name", label: "Programme" },
    { key: "major_name", label: "Major Name" },
    { key: "medium", label: "Medium of Instruction" },
];

const FINANCIAL_RESOURCE_COLS = [
    { key: "amount", label: "Amount (₹)" },
    { key: "period_from", label: "From Date" },
    { key: "period_to", label: "To Date" },
    { key: "fdr_no", label: "FDR No" },
    { key: "date", label: "Date" },
    { key: "bank_name", label: "Bank Name" },
    { key: "ifsc_code", label: "IFSC Code" },
    { key: "fdr_file", label: "FDR Document" },
];

const CORPUS_FUND_COLS = [
    { key: "programme", label: "Programme" },
    { key: "amount", label: "Amount (₹)" },
    { key: "period_from", label: "From Date" },
    { key: "period_to", label: "To Date" },
    { key: "fdr_no", label: "FDR No" },
    { key: "date", label: "Date" },
    { key: "bank_name", label: "Bank Name" },
    { key: "ifsc_code", label: "IFSC Code" },
    { key: "maturity_date", label: "Maturity Date" },
    { key: "renewal_date", label: "Renewal Date" },
    { key: "fdr_file", label: "FDR Document" },
];

const DPR_DOCUMENTS = [
    { key: "dpr_document", label: "Detailed Project Report" },
    { key: "development_plan_doc", label: "Development Plan for the College" },
    { key: "governance_structure_doc", label: "Academic Administrative Governance Structure" },
    { key: "finance_source_doc", label: "Sources of Financing" },
    { key: "faculty_policies_doc", label: "Faculty Recruitment & Development Policies" },
];

export default function NewCollegeApplPreview({ showHeader = true, collCode }) {
    const [basicDetails, setBasicDetails] = useState(null);
    const [ugPrograms, setUgPrograms] = useState([]);
    const [financialResources, setFinancialResources] = useState([]);
    const [corpusFundDetails, setCorpusFundDetails] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    const { handlePrint } = usePrintHandler();

    useEffect(() => {
        getCollegeData();
    }, [collCode]);

    const getCollegeData = async () => {
        try {
            setLoading(true);
            const userId = sessionStorage.getItem("user_id");
            const finalCollCode = collCode || userId;

            if (!finalCollCode) {
                setError("User ID not found");
                setLoading(false);
                return;
            }

            const response = await CommonAxiosGet(
                `${config.url.FAC_URL}getCollegeApplicationDetails?collCode=${finalCollCode}`
            );

            if (response && response.data) {
                if (response.data.BasicDetails && response.data.BasicDetails.length > 0) {
                    const details = response.data.BasicDetails[0];
                    setBasicDetails(details);
                }
                if (response.data.ugPrograms && Array.isArray(response.data.ugPrograms)) {
                    setUgPrograms(response.data.ugPrograms);
                }
                if (response.data.financialResource && Array.isArray(response.data.financialResource)) {
                    setFinancialResources(response.data.financialResource);
                }
                if (response.data.corpusFund && Array.isArray(response.data.corpusFund)) {
                    setCorpusFundDetails(response.data.corpusFund);
                }
                setError(null);
            } else {
                setError("No data found");
            }
        } catch (err) {
            console.error("Error fetching college data:", err);
            setError("Failed to load college application data");
        } finally {
            setLoading(false);
        }
    };

    // Helper function to format boolean values
    const formatBoolean = (value) => {
        if (value === true || value === "true") return "Yes";
        if (value === false || value === "false") return "No";
        return value || "-";
    };

    // Process basic details with formatted values
    const processedBasicDetails = basicDetails ? {
        ...basicDetails,
        is_other_institution: formatBoolean(basicDetails.is_other_institution),
        is_temp_college: formatBoolean(basicDetails.is_temp_college),
        has_criminal_record: formatBoolean(basicDetails.has_criminal_record),
        is_ugc_not_recognized: formatBoolean(basicDetails.is_ugc_not_recognized),
        has_dues_complaints: formatBoolean(basicDetails.has_dues_complaints),
        is_own_land: formatBoolean(basicDetails.is_own_land),
        is_land_contiguous: formatBoolean(basicDetails.is_land_contiguous),
        is_building_plan_approved: formatBoolean(basicDetails.is_building_plan_approved),
        is_near_restricted_area: formatBoolean(basicDetails.is_near_restricted_area),
        is_improper_building: formatBoolean(basicDetails.is_improper_building),
        is_other_institution_building: formatBoolean(basicDetails.is_other_institution_building),
        is_parking_available: formatBoolean(basicDetails.is_parking_available),
        is_parking_size_20_percent: formatBoolean(basicDetails.is_parking_size_20_percent),
        is_playground_available: formatBoolean(basicDetails.is_playground_available),
        has_alternative_playground: formatBoolean(basicDetails.has_alternative_playground),
    } : null;

    if (loading) {
        return (
            <div className="pvp-root">
                <PrintStyles />
                <div style={{ textAlign: "center", padding: "40px" }}>
                    <p>Loading college application data...</p>
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="pvp-root">
                <PrintStyles />
                <div style={{ textAlign: "center", padding: "40px", color: "red" }}>
                    <p>Error: {error}</p>
                </div>
            </div>
        );
    }

    if (!processedBasicDetails) {
        return (
            <div className="pvp-root">
                <PrintStyles />
                <div style={{ textAlign: "center", padding: "40px" }}>
                    <p>No data to display.</p>
                </div>
            </div>
        );
    }

    return (
        <div className="pvp-root">
            <PrintStyles />

            {showHeader && <PrintHeader onPrint={handlePrint} title="New College Application Preview" />}

            {/* Society Details Section */}
            <div className="pvp-section">
                <SectionHeader icon="🏛️" title="Society Details" color="#2563eb" bg="#eef3fe" />
                <FieldGrid
                    data={processedBasicDetails}
                    fields={SOCIETY_FIELDS}
                    fileFields={NEW_COLLEGE_FILE_FIELDS}
                />
            </div>

            {/* Society Status Section */}
            <div className="pvp-section">
                <SectionHeader icon="📋" title="Society Status Information" color="#d96119" bg="#fdf0e8" />
                <FieldGrid
                    data={processedBasicDetails}
                    fields={SOCIETY_STATUS_FIELDS}
                    fileFields={NEW_COLLEGE_FILE_FIELDS}
                />
            </div>

            {/* College Details Section */}
            <div className="pvp-section">
                <SectionHeader icon="🏫" title="College Details" color="#0d9e76" bg="#e6f6f2" />
                <FieldGrid
                    data={processedBasicDetails}
                    fields={COLLEGE_FIELDS}
                    fileFields={NEW_COLLEGE_FILE_FIELDS}
                />
            </div>

            {/* Secretary Contact Details */}
            <div className="pvp-section">
                <SectionHeader icon="👨‍💼" title="Secretary Contact Details" color="#4f46e5" bg="#eef2ff" />
                <FieldGrid
                    data={processedBasicDetails}
                    fields={SECRETARY_FIELDS}
                    fileFields={NEW_COLLEGE_FILE_FIELDS}
                />
            </div>

            {/* Principal Contact Details */}
            <div className="pvp-section">
                <SectionHeader icon="👨‍🏫" title="Principal Contact Details" color="#c08a10" bg="#fdf6e3" />
                <FieldGrid
                    data={processedBasicDetails}
                    fields={PRINCIPAL_FIELDS}
                    fileFields={NEW_COLLEGE_FILE_FIELDS}
                />
            </div>

            {/* Land Details Section - Only show if own land is Yes */}
            {basicDetails?.is_own_land === true && (
                <div className="pvp-section">
                    <SectionHeader icon="🏞️" title="Land Details" color="#15803d" bg="#dcfce7" />
                    <FieldGrid
                        data={processedBasicDetails}
                        fields={LAND_BASIC_FIELDS}
                        fileFields={NEW_COLLEGE_FILE_FIELDS}
                    />

                    <div className="pvp-subsection">
                        <SectionHeader icon="📍" title="Land Location Details" color="#0f766e" bg="#f0fdfa" />
                        <FieldGrid
                            data={processedBasicDetails}
                            fields={LAND_DETAILS_FIELDS}
                            fileFields={NEW_COLLEGE_FILE_FIELDS}
                        />
                    </div>

                    <div className="pvp-subsection">
                        <SectionHeader icon="📄" title="Land Documents" color="#be185d" bg="#fdf2f8" />
                        <FieldGrid
                            data={processedBasicDetails}
                            fields={LAND_DOCUMENTS_FIELDS}
                            fileFields={NEW_COLLEGE_FILE_FIELDS}
                        />
                    </div>
                </div>
            )}

            {/* Building Details Section */}
            <div className="pvp-section">
                <SectionHeader icon="🏗️" title="Building Details" color="#d63039" bg="#fdeaea" />
                <FieldGrid
                    data={processedBasicDetails}
                    fields={BUILDING_BASIC_FIELDS}
                    fileFields={NEW_COLLEGE_FILE_FIELDS}
                />

                <div className="pvp-subsection">
                    <SectionHeader icon="✓" title="Building Approval Status" color="#b45309" bg="#fff7ed" />
                    <FieldGrid
                        data={processedBasicDetails}
                        fields={BUILDING_APPROVAL_FIELDS}
                        fileFields={NEW_COLLEGE_FILE_FIELDS}
                    />
                </div>

                <div className="pvp-subsection">
                    <SectionHeader icon="📎" title="Building Documents" color="#1a56db" bg="#ebf5ff" />
                    <FieldGrid
                        data={processedBasicDetails}
                        fields={BUILDING_DOCUMENTS_FIELDS}
                        fileFields={NEW_COLLEGE_FILE_FIELDS}
                    />
                </div>
            </div>

            {/* Parking Details */}
            <div className="pvp-section">
                <SectionHeader icon="🅿️" title="Parking Details" color="#0b8fbd" bg="#e3f6fd" />
                <FieldGrid
                    data={processedBasicDetails}
                    fields={PARKING_FIELDS}
                    fileFields={NEW_COLLEGE_FILE_FIELDS}
                />
            </div>

            {/* Playground Details */}
            <div className="pvp-section">
                <SectionHeader icon="⚽" title="Playground Details" color="#0f766e" bg="#f0fdfa" />
                <FieldGrid
                    data={processedBasicDetails}
                    fields={PLAYGROUND_FIELDS}
                    fileFields={NEW_COLLEGE_FILE_FIELDS}
                />
            </div>

            {/* UG Programs Section */}
            {ugPrograms?.length > 0 && (
                <div className="pvp-section">
                    <SectionHeader icon="📚" title="Proposed Programs" color="#2563eb" bg="#eef3fe" count={ugPrograms.length} />
                    <DataTable
                        rows={ugPrograms}
                        columns={UG_PROGRAM_COLS}
                        fileFields={NEW_COLLEGE_FILE_FIELDS}
                    />
                </div>
            )}

            {/* Financial Resources Section */}
            {financialResources?.length > 0 && (
                <div className="pvp-section">
                    <SectionHeader icon="💰" title="Financial Resources" color="#0d9e76" bg="#e6f6f2" count={financialResources.length} />
                    <DataTable
                        rows={financialResources}
                        columns={FINANCIAL_RESOURCE_COLS}
                        fileFields={NEW_COLLEGE_FILE_FIELDS}
                    />
                </div>
            )}

            {/* Corpus Fund Section */}
            {corpusFundDetails?.length > 0 && (
                <div className="pvp-section">
                    <SectionHeader icon="🏦" title="Corpus Fund Details" color="#be185d" bg="#fdf2f8" count={corpusFundDetails.length} />
                    <DataTable
                        rows={corpusFundDetails}
                        columns={CORPUS_FUND_COLS}
                        fileFields={NEW_COLLEGE_FILE_FIELDS}
                    />
                </div>
            )}

            {/* DPR and Other Documents */}
            <div className="pvp-section">
                <SectionHeader icon="📋" title="DPR and Other Documents" color="#4f46e5" bg="#eef2ff" />
                <FieldGrid
                    data={processedBasicDetails}
                    fields={DPR_DOCUMENTS}
                    fileFields={NEW_COLLEGE_FILE_FIELDS}
                />
                {/* <DataTable rows={processedBasicDetails} columns={DPR_DOCUMENTS} fileFields={NEW_COLLEGE_FILE_FIELDS} /> */}
            </div>
        </div>
    );
}